/** Snap math for Tier 2 Group D workspace UI. Pure functions —
 *  no DOM, no canvas, no Fabric. Snap to guides + grid lines
 *  during Move-tool drags or any other interaction that wants to
 *  pull positions toward the user's working coordinate system.
 *
 *  The snap functions operate in **scene-space pixels** (the
 *  coordinate space `state.viewSettings.guides[].position` and
 *  `gridConfig.size` are expressed in). Callers translate
 *  screen-space drag deltas to scene-space before snapping; the
 *  return value is scene-space too. Tolerance is also scene-space
 *  — callers that want a screen-pixel tolerance should divide by
 *  the current viewport zoom before calling.
 *
 *  Returned `snappedX` / `snappedY` reflect the input's possible
 *  adjustment per axis. When the nearest guide/grid line is
 *  beyond `tolerance`, the corresponding axis returns the input
 *  unchanged. The `matched*` fields tell callers which features
 *  triggered (so a future snap-line render can highlight the
 *  matching guide / grid line in magenta).
 *
 *  Snap semantics: BOTH axes are snapped independently. The
 *  horizontal-guide and grid-row contribute Y candidates; the
 *  vertical-guide and grid-column contribute X candidates. A
 *  point near a guide intersection snaps in both axes
 *  simultaneously. */

import type { Guide } from '../types/image-editor.types';

export interface SnapOptions {
  /** Scene-space distance within which a candidate line "pulls"
   *  the point. Callers using screen-pixel tolerance should
   *  divide by current zoom (e.g. `8 / zoom`). 0 = no snap. */
  tolerance: number;
  /** When true and `guides` is non-empty, X / Y candidates from
   *  vertical / horizontal guides participate. */
  snapToGuides: boolean;
  /** When true, X / Y candidates from grid columns / rows at
   *  multiples of `gridSize` participate. */
  snapToGrid: boolean;
  /** Scene-space grid spacing. Ignored when `snapToGrid` is
   *  false. Must be positive — a 0 or negative `gridSize` is
   *  treated as no-grid. */
  gridSize: number;
  /** Guide list to test against. Ignored when `snapToGuides` is
   *  false. Empty array is a no-op. */
  guides: Guide[];
}

export interface SnapResult {
  snappedX: number;
  snappedY: number;
  /** Id of the vertical guide that snapped X, or `'grid'` for a
   *  grid column, or `null` if X wasn't snapped. */
  matchedX: string | 'grid' | null;
  matchedY: string | 'grid' | null;
}

/** Snap a scene-space point to nearest guide / grid line on each
 *  axis independently. */
export function snapPoint(x: number, y: number, opts: SnapOptions): SnapResult {
  const xCand = nearestCandidateForAxis(
    x,
    'vertical',
    opts.guides,
    opts.snapToGuides,
    opts.snapToGrid,
    opts.gridSize,
    opts.tolerance,
  );
  const yCand = nearestCandidateForAxis(
    y,
    'horizontal',
    opts.guides,
    opts.snapToGuides,
    opts.snapToGrid,
    opts.gridSize,
    opts.tolerance,
  );
  return {
    snappedX: xCand ? xCand.position : x,
    snappedY: yCand ? yCand.position : y,
    matchedX: xCand ? xCand.matched : null,
    matchedY: yCand ? yCand.matched : null,
  };
}

interface AxisCandidate {
  position: number;
  matched: string | 'grid';
}

/** Find the closest guide or grid line within tolerance for one
 *  axis. Returns null when nothing is in range.
 *
 *  Guides **preempt** grid: if ANY guide is in tolerance on this
 *  axis, the grid is ignored entirely (not just losing on a tie).
 *  This is what kills the candidate-switch jitter the user sees
 *  when both toggles are on: without preemption, the closest
 *  guide and the closest grid line could both be in tolerance,
 *  and the "winner" would flip between them as the cursor nudges
 *  by sub-pixel amounts — object bounces between two snap
 *  positions every frame. Guides are explicit user intent; grid
 *  is background structure. Photoshop follows the same rule. */
function nearestCandidateForAxis(
  value: number,
  axisForGuides: 'horizontal' | 'vertical',
  guides: Guide[],
  snapToGuides: boolean,
  snapToGrid: boolean,
  gridSize: number,
  tolerance: number,
): AxisCandidate | null {
  if (tolerance <= 0) return null;

  // First pass: guides on this axis. Keep the closest. If any
  // guide is in tolerance, we're locked to guides — grid is
  // skipped entirely.
  let bestGuide: AxisCandidate | null = null;
  let bestGuideDist = tolerance + 1;
  if (snapToGuides) {
    for (const g of guides) {
      if (g.axis !== axisForGuides) continue;
      const dist = Math.abs(g.position - value);
      if (dist > tolerance) continue;
      if (dist < bestGuideDist) {
        bestGuideDist = dist;
        bestGuide = { position: g.position, matched: g.id };
      }
    }
  }
  if (bestGuide) return bestGuide;

  // Second pass: only when no guide matched on this axis. Grid
  // lines are at multiples of gridSize; nearest is `round(v/s)*s`.
  if (snapToGrid && gridSize > 0) {
    const nearestLine = Math.round(value / gridSize) * gridSize;
    const dist = Math.abs(nearestLine - value);
    if (dist <= tolerance) {
      return { position: nearestLine, matched: 'grid' };
    }
  }

  return null;
}

/** Snap an axis-aligned bounding box by its NEAREST anchor —
 *  picks whichever of (left edge, center, right edge) for X and
 *  (top, center, bottom) for Y is closest to a snap target. This
 *  mirrors Photoshop's "smart" snap-to-guides behavior: dragging
 *  near a guide pulls whichever bbox edge is closest to it, not
 *  always the same anchor.
 *
 *  Returns the bbox's top-left after snapping (since callers
 *  typically reposition objects by setting top-left). */
export function snapBoundingBox(
  left: number,
  top: number,
  width: number,
  height: number,
  opts: SnapOptions,
): {
  left: number;
  top: number;
  matchedX: string | 'grid' | null;
  matchedY: string | 'grid' | null;
} {
  const right = left + width;
  const centerX = left + width / 2;
  const bottom = top + height;
  const centerY = top + height / 2;

  const xCandidates: { value: number; offset: number }[] = [
    { value: left, offset: 0 },
    { value: centerX, offset: -width / 2 },
    { value: right, offset: -width },
  ];
  const yCandidates: { value: number; offset: number }[] = [
    { value: top, offset: 0 },
    { value: centerY, offset: -height / 2 },
    { value: bottom, offset: -height },
  ];

  let bestX: { newLeft: number; dist: number; matched: string | 'grid' } | null = null;
  for (const cand of xCandidates) {
    const res = nearestCandidateForAxis(
      cand.value,
      'vertical',
      opts.guides,
      opts.snapToGuides,
      opts.snapToGrid,
      opts.gridSize,
      opts.tolerance,
    );
    if (!res) continue;
    const dist = Math.abs(res.position - cand.value);
    if (!bestX || dist < bestX.dist) {
      bestX = { newLeft: res.position + cand.offset, dist, matched: res.matched };
    }
  }

  let bestY: { newTop: number; dist: number; matched: string | 'grid' } | null = null;
  for (const cand of yCandidates) {
    const res = nearestCandidateForAxis(
      cand.value,
      'horizontal',
      opts.guides,
      opts.snapToGuides,
      opts.snapToGrid,
      opts.gridSize,
      opts.tolerance,
    );
    if (!res) continue;
    const dist = Math.abs(res.position - cand.value);
    if (!bestY || dist < bestY.dist) {
      bestY = { newTop: res.position + cand.offset, dist, matched: res.matched };
    }
  }

  return {
    left: bestX ? bestX.newLeft : left,
    top: bestY ? bestY.newTop : top,
    matchedX: bestX ? bestX.matched : null,
    matchedY: bestY ? bestY.matched : null,
  };
}
